<template>
  <ModalPage @close="doClose" class="calendar-modal-item">

    <template v-slot:default>
      <!-- APPOINTMENT -->
      <div v-if="isAppointment(item)" 
        class="calendar-modal-grid calendar-modal-appointment">
          <div class="calendar-icon-container"
            :class="itemTrackClasses(item)"
          >
            <CalendarIcon :item="item" :me="me" class="large" />
          </div>
          <div class="calendar-modal-title">
            {{ item.name }}
          
            <div v-if="visibleTrackName" class="calendar-modal-track" >
              {{visibleTrackName}}
            </div>
          </div>      

          <CalendarEventFill class="col-label" title="Time" />
          <div class="col-text"><!-- calendar-modal-time -->
            {{ asDateOnly(item.start_time_utc, timezone) }}
            {{ asTimeOnly(item.start_time_utc, timezone) }}
            <span> - </span>
            {{ asTimeOnly(item.end_time_utc, timezone) }}
          </div>

          <template v-if="item.location">
            <GeoAltFill class="location-label col-label" title="Location" />
            <div class="location-text col-text" v-html="formatRawText(item.location)"></div>
          </template>

          <template v-if="item.appointment_attendees">
            <CalendarIcon :force="'ticketed'" class="col-label" />
            <div class="col-text">You are attending this.</div>
          </template>

          <template v-if="item.abstract">
            <InfoCircleFill class="abstract-label col-label" title="Abstract" />
            <div class="abstract-text col-text" v-html="item.abstract"></div>
          </template>
      </div>

      <!-- MEETING -->
      <div v-else class="calendar-modal-grid calendar-modal-meeting">
          <div class="calendar-icon-container">
            <CalendarIcon :item="item" :me="me" class="large" />
          </div>

          <div class="calendar-modal-title">
            <span v-if="Filter.isMeetingConfirmed(item)">Confirmed Meeting</span>
            <span v-else-if="rescheduleTo">Rescheduled Meeting</span>
            <span v-else-if="Filter.isDeleted(item)">Cancelled Meeting</span>
            <span v-else-if="Filter.isMeetingPendingBookerId(item, me.id)">My Requested Meeting</span>
            <span v-else-if="Filter.isMeetingPendingAttendeeId(item, me.id)">Requested Meeting</span>
            
          </div>

          <div v-if="rescheduleFrom" class="col-text"
            @click="doModal(rescheduleFrom)"
          >
            <span class="btn btn-small">
              Rescheduled from: 
              <span v-if="rescheduleFrom.start_time_utc">
                {{ asDateOnly(rescheduleFrom.start_time_utc, timezone) }}
                {{ asTimeOnly(rescheduleFrom.start_time_utc, timezone) }}
                <span> - </span>
                {{ asTimeOnly(rescheduleFrom.end_time_utc, timezone) }}
              </span>
              <span v-else><i class="item-time-pending">Pending</i></span>
            </span>
          </div>

          <div v-if="rescheduleTo" class="col-text"
            @click="doModal(rescheduleTo)"
          >
            <span class="btn btn-small btn-info">
              Rescheduled to: 
              <span v-if="rescheduleTo.start_time_utc">
                {{ asDateOnly(rescheduleTo.start_time_utc, timezone) }}
                {{ asTimeOnly(rescheduleTo.start_time_utc, timezone) }}
                <span> - </span>
                {{ asTimeOnly(rescheduleTo.end_time_utc, timezone) }}
              </span>
              <span v-else><i class="item-time-pending">Pending</i></span>
            </span>
          </div>

          <CalendarEventFill class="col-label" title="Time" />
          <div class="col-text"
            :class="{'calendar-modal-time-previous': rescheduleTo}"
          >
            <div v-if="item.start_time_utc">
              {{ asDateOnly(item.start_time_utc, timezone) }}
              {{ asTimeOnly(item.start_time_utc, timezone) }}
              <span> - </span>
              {{ asTimeOnly(item.end_time_utc, timezone) }}
            </div>
            <div v-else><i class="item-time-pending">Pending</i></div>
          </div>
          

          <template v-if="!Filter.isDeleted(item)">
            <GeoAltFill class="location-label col-label" title="Location" />
            <div class="location-text col-text" v-html="formatRawText(meetingPointName(item, '<i>Pending</i>'))"></div>
          </template>
          
        
          <!-- <hr class="col-both calendar-hr"> -->
          <h5 class="col-both">Booker</h5>
          <ProfileWidget
            class="col-both"
            :id="item.booker_id"
            :title="item.booker_name"
            :subtitle="item.booker_organisation_name"
            :fields="item.booker_fields"
            :rows="2"
          />
          <h5 class="col-both">Recipient</h5>
          <ProfileWidget
            class="col-both"
            :id="item.attendee_id"
            :title="item.attendee_name"
            :subtitle="item.attendee_organisation_name"
            :fields="item.attendee_fields"
            :rows="2"
          />

          <h5 v-if="item.colleagues.length" 
            class="calendar-modal-item-colleagues col-both">
            <span>Also attending</span>
            <span class="also-attending-count">+ {{item.colleagues.length}}</span>       
          </h5>
          <ProfileWidget v-for="colleague in item.colleagues" :key="colleague.id"
            class="col-both"
            :id="colleague.attendee_id"
            :title="colleague.full_name"
            :subtitle="colleague.organisation_name"
            :rows="2"
          />
      </div>

    </template>

    <!-- FOOTER -->
    <template v-slot:footer>
      <div class="calendar-modal-footer">
        <button
          @click="doModal(prevItem)"
          :disabled="!Boolean(prevItem)"
          class="btn"
          title="Next item"
        >
          <i class="icon-chevron-left"></i>
        </button>

        <a :href="url" class="col-text btn btn-primary btn-calendar-modal"><span>Details</span></a>

        <button
          @click="doModal(nextItem)"
          :disabled="!Boolean(nextItem)"
          class="btn"
          title="Next item"
        >
          <i class="icon-chevron-right"></i>
        </button>
      </div>
    </template>
  </ModalPage>
</template>

<script>
import { mapGetters } from "vuex";

import { asDateOnly, asTimeOnly } from "@/components/DateTime";

import { isAppointment, Filter } from "@/utils/Filter";

import CalendarEventFill from '@/components/icons/CalendarEventFill';
import GeoAltFill from '@/components/icons/GeoAltFill';
import InfoCircleFill from '@/components/icons/InfoCircleFill';

import ModalPage from "@/components/ModalPage";
import ProfileWidget from "@/components/ProfileWidget";
import {itemTrackClasses} from '@/utils/Attributes';
import {meetingPointName} from '@/utils/Item';
import {formatRawText} from '@/utils/Text';
import CalendarIcon from "./CalendarIcon";
import { TRACK_HIDDEN } from "./config";

export default {
  name: "CalendarModalItem",
  components: {
    CalendarIcon,
    CalendarEventFill,
    GeoAltFill,
    InfoCircleFill,
    ModalPage,
    ProfileWidget,
  },
  props: {
    item: {
      type: Object,
      required: true,
    },
    items: {
      type: Array,
      required: true,
    },
    timezone: {
      // If no timezone is passed, don't show the time field
      type: String,
      required: false,
    },
    me: {
      type: Object,
      required: false,
    },
  },
  methods: {
    asDateOnly,
    asTimeOnly,
    isAppointment,
    formatRawText,
    meetingPointName,
    itemTrackClasses,
    doClose() {
      this.$emit("close");
    },
    doModal(item) {
      this.$emit("modal", {
        item: item,
        items: this.items
      });
    },
  },
  computed: {
    curIndex() {
      // We're naively comparing Meeting/Appointment.id values, but they're in v.different ranges now
      return this.items.findIndex((cur) => cur.id === this.item.id);
    },
    nextItem() {
      if (this.curIndex !== -1) {
        let nextIndex = this.curIndex + 1;
        if (this.items[nextIndex]) {
          return this.items[nextIndex];
        }
      }
      return null;
    },
    prevItem() {
      if (this.curIndex !== -1) {
        let prevIndex = this.curIndex - 1;
        if (prevIndex >= 0 && this.items[prevIndex]) {
          return this.items[prevIndex];
        }
      }
      return null;
    },
    isDeleted() {
        return Filter.isDeleted(this.item);
    },
    rescheduleTo() {
      if (this.item.reschedule_to_id) {
        return this.meetingById(this.item.reschedule_to_id);
      }
      return null;
    },
    rescheduleFrom() {
      if (this.item.reschedule_from_id) {
        return this.meetingById(this.item.reschedule_from_id);
      }
      return null;
    },
    basePath() {
      if (window.location.pathname.startsWith('/admin')) {
        return "/admin";
      }
      return "";
    },
    urlAppointment() {
        return `${this.basePath}/appointments/${this.item.id}`;
    },
    urlMeeting() {
        return `${this.basePath}/book/show?id=${this.item.id}`;
    },
    url() {
        if (isAppointment(this.item)) {
            return this.urlAppointment;
        } else {
            return this.urlMeeting;
        }
    },
    visibleTrackName() {
      if (this.item.track_name && this.item.track_name !== TRACK_HIDDEN) {
        return this.item.track_name;
      }
      return null;
    },
    ...mapGetters({
      meetingById: 'meetings/meetingById',
    })
  },
  beforeCreate() {
    this.Filter = Filter;
  },
};
</script>
<style>
.calendar-modal-footer {
  display: flex;
  justify-content: space-between;
  gap: 20px;
}

.btn-calendar-modal {
  flex-grow: 1;
}

.calendar-modal-grid {
    display: grid;
    grid-template-columns: [col-label] 40px [col-text] auto;
    gap: 10px 10px;
}
.col-label {
    grid-column: col-label;
    align-self: start;
    justify-self: center;
}
.col-text {
    grid-column: col-text;
}
.col-both {
    /* grid-column: col-label / col-text; */
    grid-column: 1 / -1;
}

#calendar-app .calendar-modal-grid .calendar-icon-container {
    grid-column: col-label;
    grid-row: 1;
    width: 40px;
    height: 40px;
    margin: 0;
    justify-self: top;
}
.calendar-icon-container .calendar-icon {
    display: block;
    height: 100%;
    width: 100%;
}
.calendar-icon-container .calendar-icon svg {
    height: 100%;
    width: 100%;
}

.calendar-modal-grid .calendar-modal-title {
    display: block;
    grid-column: col-text;
    grid-row: 1;
    font-weight: bold;
    font-size: 26px;
    line-height: 26px;
    align-self: center;
}
.calendar-modal-grid .calendar-modal-track {
    font-size: 14px;
    line-height: 16px;
}

.calendar-modal-grid .calendar-modal-time {
    grid-column: col-text;
    grid-row: 2;
}

.calendar-modal-item .location-text {
    word-break: break-all;
}

.calendar-modal-item h5 {
  margin-bottom: 0;
}

.calendar-modal-item .also-attending-count {
  display: inline-block;
  margin-left: 5px;
  padding: 2px 3px;
  border-radius: 3px;
  font-size: 12px;
  font-weight: normal;
  line-height: 16px;
  background: #666;
  color: #fff;
}

/* Profile hints */
.calendar-modal-item .profile-widget-core {
  min-width: 200px;
}
.calendar-modal-item .profile-fields {
  flex-grow: 1;
}




.calendar-modal-time {
  font-size: 14px;
  line-height: 14px;
  font-weight: normal;
}

.calendar-modal-time-previous {
  text-decoration: line-through;
}
/* .calendar-modal-title {
    display: flex;
} */
/* .calendar-modal-title span {
    flex-grow: 1;
    max-width: calc(100% - 50px);
} */
#calendar-app .calendar-modal-title .calendar-icon {
  height: 40px;
  width: 40px;
  margin-right: 10px;
  margin-top: 0;
}

/* .calendar-modal-title .profile-widget {

} */
</style>